import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';

import { FieldRow, TextInputField } from '@/features/store-settings';

import {
  hasWorkingHoursRestriction,
  isEveryDayOff,
  parseWorkingHours,
  seedWorkingHours,
  serializeWorkingHours,
} from '../../model/workingHours';
import type { ChatbotWorkingHoursDay } from '../../model/workingHours';

export interface WorkingHoursFieldProps {
  value: Record<string, unknown> | null;
  onChange: (value: Record<string, unknown>) => void;
  disabled?: boolean;
  /** *(delta 2026-09-23)* The provenance chip + row actions slot (§17.2) — rendered next to the
   * field's own label since `WorkingHoursField` renders its own `FieldRow`s and carried no
   * provenance before. */
  labelAdornment?: ReactNode;
  /** The tenant's timezone, when the page can read it (`settings.tenant.view`) — seeds "Set working
   * hours" (design §17.1.6). */
  tenantTimezone?: string;
  canManage?: boolean;
  /** Fires whenever the "timezone required" error starts/stops applying, so the card can disable
   * Save — a restricted schedule with no timezone would otherwise save silently. */
  onValidityChange?: (valid: boolean) => void;
}

const WEEKDAY_LABEL_KEYS = [
  'settings:options.weekday.monday',
  'settings:options.weekday.tuesday',
  'settings:options.weekday.wednesday',
  'settings:options.weekday.thursday',
  'settings:options.weekday.friday',
  'settings:options.weekday.saturday',
  'settings:options.weekday.sunday',
];

/**
 * `workingHours` is an untyped BFF `object` — this renders the hub's real shape
 * (`domain-conversation`: `{ timezone, days: [{ dayOfWeek, open, close, enabled }] }`).
 * *(delta 2026-09-23)* `{}` (the platform default) now renders as "No working hours set" instead of
 * seven switched-off days — the shipped defect design §17.1.6 closes (the opposite reading of what
 * the hub does: an absent/unreadable schedule is NO restriction, not "closed all week").
 */
export function WorkingHoursField({
  value,
  onChange,
  disabled = false,
  labelAdornment,
  tenantTimezone,
  canManage = true,
  onValidityChange,
}: WorkingHoursFieldProps) {
  const { t } = useTranslation('chatbot');
  const hasRestriction = hasWorkingHoursRestriction(value);
  const parsed = parseWorkingHours(value);
  // The timezone-required error appears only once the merchant has actually edited the timezone
  // field — never on first render of a freshly-seeded schedule.
  const [timezoneTouched, setTimezoneTouched] = useState(false);
  const timezoneMissingNow = hasRestriction && parsed.timezone.trim() === '' && timezoneTouched;
  useEffect(() => {
    onValidityChange?.(!timezoneMissingNow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezoneMissingNow]);

  const updateDay = (dayOfWeek: number, patch: Partial<ChatbotWorkingHoursDay>) => {
    const days = parsed.days.map((day) =>
      day.dayOfWeek === dayOfWeek ? { ...day, ...patch } : day,
    );
    onChange(serializeWorkingHours({ ...parsed, days }));
  };

  const copyMondayToWeekdays = () => {
    const monday = parsed.days.find((day) => day.dayOfWeek === 1);
    if (!monday) return;
    const days = parsed.days.map((day) =>
      day.dayOfWeek >= 2 && day.dayOfWeek <= 5
        ? { ...day, open: monday.open, close: monday.close, enabled: monday.enabled }
        : day,
    );
    onChange(serializeWorkingHours({ ...parsed, days }));
  };

  const setHours = () => {
    onChange(serializeWorkingHours(seedWorkingHours(tenantTimezone ?? '')));
  };

  if (!hasRestriction) {
    return (
      <FieldRow
        label={
          <Stack direction="row" spacing={1} alignItems="center">
            {labelAdornment ?? t('escalation.workingHours.label')}
          </Stack>
        }
        description={t('escalation.workingHours.description')}
        alignTop
      >
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            {t('escalation.workingHours.noRestriction')}
          </Typography>
          {canManage ? (
            <Button
              size="small"
              onClick={setHours}
              disabled={disabled}
              sx={{ alignSelf: 'flex-start' }}
            >
              {t('escalation.workingHours.setHours')}
            </Button>
          ) : null}
        </Stack>
      </FieldRow>
    );
  }

  const allDaysOff = isEveryDayOff(parsed);

  return (
    <Stack spacing={1.5}>
      <TextInputField
        label={t('escalation.workingHours.timezoneLabel')}
        description={t('escalation.workingHours.timezoneDescription')}
        value={parsed.timezone}
        onChange={(next) => {
          setTimezoneTouched(true);
          onChange(serializeWorkingHours({ ...parsed, timezone: next }));
        }}
        disabled={disabled}
        placeholder="Europe/Moscow"
        error={timezoneMissingNow}
        helperText={timezoneMissingNow ? t('escalation.workingHours.timezoneRequired') : undefined}
      />
      <FieldRow
        label={
          <Stack direction="row" spacing={1} alignItems="center">
            {labelAdornment ?? t('escalation.workingHours.label')}
          </Stack>
        }
        description={t('escalation.workingHours.description')}
        alignTop
      >
        <Stack spacing={1}>
          {allDaysOff ? (
            <Typography variant="caption" color="warning.main">
              {t('escalation.workingHours.allDaysOff')}
            </Typography>
          ) : null}
          {parsed.days.map((day, index) => {
            const dayLabel = t(WEEKDAY_LABEL_KEYS[index]);
            return (
              <Stack
                key={day.dayOfWeek}
                component="fieldset"
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ border: 'none', p: 0, m: 0 }}
              >
                <Typography component="legend" variant="body2" sx={{ width: 96, flexShrink: 0 }}>
                  {dayLabel}
                </Typography>
                <Switch
                  size="small"
                  checked={day.enabled}
                  disabled={disabled}
                  onChange={(event) => updateDay(day.dayOfWeek, { enabled: event.target.checked })}
                  slotProps={{ input: { 'aria-label': dayLabel } }}
                />
                <TextField
                  size="small"
                  type="time"
                  value={day.open}
                  disabled={disabled || !day.enabled}
                  onChange={(event) => updateDay(day.dayOfWeek, { open: event.target.value })}
                  slotProps={{
                    htmlInput: {
                      'aria-label': t('escalation.workingHours.from', { day: dayLabel }),
                    },
                  }}
                  sx={{ width: 130 }}
                />
                <TextField
                  size="small"
                  type="time"
                  value={day.close}
                  disabled={disabled || !day.enabled}
                  onChange={(event) => updateDay(day.dayOfWeek, { close: event.target.value })}
                  slotProps={{
                    htmlInput: { 'aria-label': t('escalation.workingHours.to', { day: dayLabel }) },
                  }}
                  sx={{ width: 130 }}
                />
              </Stack>
            );
          })}
          <Button
            size="small"
            onClick={copyMondayToWeekdays}
            disabled={disabled}
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('escalation.workingHours.copyMonday')}
          </Button>
        </Stack>
      </FieldRow>
    </Stack>
  );
}
